import classNames from 'classnames/bind';
import { List } from 'immutable';
import React, { useState } from 'react';
import { Row, Col } from 'react-flexbox-grid';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { Breadcrumbs } from '@saucelabs/ds-breadcrumbs';
import { HStack, VStack } from '@saucelabs/sl-component-grid';
import { ContentLoader } from '@saucelabs/sl-component-loader';
import { Typography } from '@saucelabs/ds-typography';
import { STANDARD_EVENTS, trackStandardEvent } from '@saucelabs/sl-util-segmentio';

import { ROUTE_PARENT_SECTION, CANCEL_SUBSCRIPTION_SURVEY_MODAL } from '../../constants';
import { Subscription } from '../../records/Subscription';
import { UsageSubscriptionCancelForm } from './UsageSubscriptionCancelForm';
import ContainerStyles from '../../components/Layout/GridContainer.styles';
import Styles from './SubscriptionCancelForm.styles';
import CancelSubscriptionSurveyModal from './CancelSubscriptionSurveyModal';
import { PRODUCT_AREA, PRODUCT_SUB_AREAS, SUBJECT } from '../../tracking';
import { SubscriptionCancelForm } from './SubscriptionCancelForm';
import BackToPlatformLink from '../BackToPlatformLink';
import SideNavigation from '../SideNavigation';

const PARENT_URL = `/${ROUTE_PARENT_SECTION}`;

const crumbList = [
  { name: 'Subscription and Billing', url: PARENT_URL },
  { name: 'Cancel Subscription' },
];

export interface Props {
  cancelSubscriptions: (ids: string[]) => Promise<void>;
  fetchSubscriptions: () => void;
  showModal: (string) => void;
  subscriptions: List<Subscription>;
}

const context = classNames.bind(Styles);

export const CancelSubscription: React.FC<Props> = ({
  cancelSubscriptions,
  showModal,
  fetchSubscriptions,
  subscriptions = List(),
}) => {
  const initialized = subscriptions?.size > 0;
  const [cancelSubscriptionIds, setCancelSubscriptionIds] = useState<string[]>([]);

  const history = useHistory();

  const displayModal = (subscriptionIds: string[]) => {
    // setup subscriptionIds
    setCancelSubscriptionIds(subscriptionIds);
    showModal(CANCEL_SUBSCRIPTION_SURVEY_MODAL);
    trackStandardEvent(
      STANDARD_EVENTS.OBJECT_CLICKED,
      PRODUCT_AREA,
      PRODUCT_SUB_AREAS.CANCELLATION,
      SUBJECT.SHOW_CANCELLATION_SURVEY_MODAL,
    );
  };

  const onConfirmCancelSubscription = async () => {
    await cancelSubscriptions(cancelSubscriptionIds);
    history.push(PARENT_URL);
  };

  const onHideCancelSubscription = () => {
    history.push(PARENT_URL);
  };

  React.useEffect(() => {
    if (!initialized) {
      fetchSubscriptions();
    }
  });

  const usageSubscription = subscriptions.find((sub) => sub.plan.isUsage());

  return (
    <div>
      <Helmet>
        <title>Cancel Subscription</title>
      </Helmet>
      <Breadcrumbs
        list={crumbList}
        prefixComponent={<BackToPlatformLink />}
      >
        <SideNavigation
          showCreditsFAQ
        />
      </Breadcrumbs>
      <div className={ContainerStyles.container}>
        <ContentLoader show={!initialized}>
          <CancelSubscriptionSurveyModal
            onConfirmCancelSubscription={onConfirmCancelSubscription}
            onHideCancelSubscription={onHideCancelSubscription}
            planType={usageSubscription ? 'usage' : 'concurrency'}
          />
          <Row>
            <Col lg={9}>
              <VStack gap={3}>
                <div>
                  <HStack
                    alignItems="center"
                    gap={3}
                  >
                    <Typography
                      className="text-high-emphasis mb-1 mt-0"
                      disableSpacing
                      variant="heading-1"
                    >
                      Cancel Subscription
                    </Typography>
                  </HStack>
                  <Typography
                    className="text-high-emphasis"
                    variant="body"
                  >
                    We&apos;re sorry to see you go!
                  </Typography>
                </div>
                <div className={context('box-with-image')}>
                  {subscriptions.size === 1 && usageSubscription ? (
                    <UsageSubscriptionCancelForm
                      displayModal={displayModal}
                      subscription={usageSubscription}
                    />
                  ) : (
                    <SubscriptionCancelForm
                      displayModal={displayModal}
                      subscriptions={subscriptions}
                    />
                  )}
                </div>
              </VStack>
            </Col>
          </Row>
        </ContentLoader>
      </div>
    </div>
  );
};
